import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Bed, Bath, CheckCircle, Clock } from 'lucide-react';
import clsx from 'clsx';
import ScoreGauge from './ScoreGauge';
import YieldMeter from './YieldMeter';
import PriceBandBadge from './PriceBandBadge';
import AreaBadge from './AreaBadge';
import WatchlistButton from './WatchlistButton';
import { pickPrimaryImage } from '../../utils/propertyImages';
import { hasPriceReduction, formatPriceReduction } from '../../utils/listingPrice';
import PropertyImage from './PropertyImage';
import { formatCurrency, propertyTypeIcon } from '../../utils/formatters';

const VERDICT_STYLE = {
  STRONG_BUY: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  BUY:        'bg-teal-500/20 text-teal-300 border-teal-500/40',
  HOLD:       'bg-amber-500/20 text-amber-300 border-amber-500/40',
  AVOID:      'bg-red-500/20 text-red-300 border-red-500/40',
};

const SOURCE_BADGE_STYLE = {
  'Online Listing': 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  'Deal':           'bg-purple-500/20 text-purple-300 border-purple-500/40',
  'Agent':          'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  'Auction':        'bg-amber-500/20 text-amber-300 border-amber-500/40',
  'Private Sale':   'bg-pink-500/20 text-pink-300 border-pink-500/40',
};

export default function PropertyCard({ property, personalScore, viewerAccess }) {
  const navigate = useNavigate();
  const score = property.score;
  const verdict = property.ai_insight?.verdict;
  const gated = viewerAccess === 'browsing' || (!score && !personalScore);

  return (
    <div
      onClick={() => navigate(`/properties/${property.id}`)}
      className={clsx(
        'relative bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 hover:border-slate-600 hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5',
        property.is_reviewed && 'opacity-60'
      )}
    >
      <PropertyImage
        src={pickPrimaryImage(property)}
        alt={property.address}
        wrapperClassName="h-36 w-full overflow-hidden bg-slate-800"
      />

      {/* Watchlist heart — absolute on the OUTER card so it sits in the
          top-right corner whether the property has an image or not. */}
      <div className="absolute top-2 right-2 z-10">
        <WatchlistButton
          propertyId={property.id}
          size={16}
          className="bg-slate-900/80 backdrop-blur-sm hover:bg-slate-800 shadow-md"
        />
      </div>

      {/* Card body */}
      <div className="p-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">{propertyTypeIcon(property.property_type)}</span>
            {property.source_label && (
              <span className={clsx('inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border', SOURCE_BADGE_STYLE[property.source_label] || 'bg-slate-500/20 text-slate-300 border-slate-500/40')}>
                {property.source_label}
              </span>
            )}
            {score && <PriceBandBadge band={score.price_band} size="sm" />}
            <AreaBadge property={property} />
            {property.status === 'stc' && (
              <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-amber-500/20 text-amber-300 border-amber-500/40">
                STC
              </span>
            )}
            {verdict && (
              <span className={clsx('inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border', VERDICT_STYLE[verdict])}>
                AI: {verdict.replace('_', ' ')}
              </span>
            )}
            {property.has_article4 === true && (
              <span
                title={
                  property.article_4_notes
                    ? `Article 4 direction: ${property.article_4_notes}. HMO conversion likely needs planning permission — confirm scope with the local council.`
                    : 'Property is inside an Article 4 direction area — HMO conversion likely needs planning permission. Confirm scope with the local council.'
                }
                className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-red-500/20 text-red-300 border-red-500/40"
              >
                HMO · A4
              </span>
            )}
            {property.has_article4 === false && (
              <span
                title="No Article 4 direction detected at this address. HMO use of dwellinghouses (3–6 unrelated occupants) generally permitted without planning permission, but always check selective licensing with the local council."
                className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
              >
                HMO · OK
              </span>
            )}
            {property.is_listed_building && (() => {
              const dist = property.listed_building_distance_m;
              const grade = property.is_listed_building;
              const veryClose = dist == null || dist <= 30;
              const label = veryClose ? `Listed · ${grade}` : `Listed · ${grade} nearby`;
              const distText = dist != null
                ? (veryClose
                    ? `at or directly adjacent (${dist}m)`
                    : `${dist}m away`)
                : 'in the immediate vicinity';
              const tooltip = (
                `A Grade ${grade} listed building is ${distText}. ` +
                `${veryClose ? 'Significant internal or external alterations require listed-building consent.' : ''}` +
                `${veryClose ? ' ' : ''}Worth checking before any major works on this property — neighbouring listed-building status can affect planning permissions, conservation rules, and what extensions / external changes are allowed.`
              ).trim();
              return (
                <span
                  title={tooltip}
                  className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-purple-500/20 text-purple-300 border-purple-500/40 cursor-help"
                >
                  {label}
                </span>
              );
            })()}
            {property.is_reviewed && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                <CheckCircle size={11} /> Reviewed
              </span>
            )}
          </div>
          <h3 className="text-slate-200 font-semibold text-sm leading-snug truncate">
            {property.address}
          </h3>
          <div className="flex items-center gap-1 mt-1 text-slate-500 text-xs">
            <MapPin size={11} />
            <span>{property.postcode}</span>
            {property.town && <span>· {property.town}</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-emerald-400 font-bold text-lg">{formatCurrency(property.asking_price, true)}</div>
          {hasPriceReduction(property) && (
            <div className="text-[10px] text-emerald-400/90 font-medium leading-tight mt-0.5 max-w-[120px] ml-auto">
              {formatPriceReduction(property)}
            </div>
          )}
          {score?.estimated_value && (
            <div className="text-slate-500 text-xs">
              Est. {formatCurrency(score.estimated_value, true)}
            </div>
          )}
        </div>
      </div>

      {/* Specs */}
      <div className="flex items-center gap-4 mb-4 text-slate-400 text-xs">
        {property.bedrooms && (
          <span className="flex items-center gap-1"><Bed size={12} />{property.bedrooms} bed</span>
        )}
        {property.bathrooms && (
          <span className="flex items-center gap-1"><Bath size={12} />{property.bathrooms} bath</span>
        )}
        <span className="flex items-center gap-1 ml-auto">
          <Clock size={11} />
          {property.date_found ? new Date(property.date_found).toLocaleDateString('en-GB') : '—'}
        </span>
      </div>

      {/* Meters */}
      <div className="flex items-center justify-around pt-3 border-t border-slate-800 relative min-h-[100px]">
        {gated ? (
          <div className="text-center py-2 px-4">
            <p className="text-slate-400 text-xs mb-1">Scores &amp; AI insights</p>
            <p className="text-emerald-400 text-sm font-semibold">Unlock with Basic or Pro</p>
            <p className="text-slate-500 text-[10px] mt-1">7-day free trial available</p>
          </div>
        ) : (
          <>
        {personalScore != null ? (
          <ScoreGauge score={personalScore} size={80} label="Your Score" />
        ) : (
          <ScoreGauge score={score?.investment_score} size={80} label="Score" />
        )}
        <YieldMeter yieldPct={score?.gross_yield_pct} size={80} label="Yield" />
        {score && (
          <div className="text-center">
            <div className="text-slate-400 text-xs mb-1">vs Market</div>
            {score.price_deviation_pct != null ? (
              <div className={clsx(
                'text-xl font-bold',
                score.price_deviation_pct < 0 ? 'text-emerald-400' : 'text-red-400'
              )}>
                {score.price_deviation_pct < 0 ? '' : '+'}
                {(score.price_deviation_pct * 100).toFixed(1)}%
              </div>
            ) : (
              <div className="text-slate-600 text-xl font-bold">—</div>
            )}
            <div className="text-slate-500 text-xs">deviation</div>
          </div>
        )}
          </>
        )}
      </div>

      </div> {/* end card body */}
    </div>
  );
}
